import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useFormikContext } from "formik";
import useSWRGas from "../../../lib/gases/useSWRGas";
import React, { useMemo } from "react";
import { resolveMaxAllowedAmount } from "./helpers";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../components/shadcn/tooltip";
import { useSelectedAccount } from "../../../context/swapAccounts";
import { useBalance } from "../../../lib/balances/useBalance";
import useWallet from "../../../hooks/useWallet";
import { useUsdModeStore } from "../../../stores/usdModeStore";
import { skipNextUsdSync } from "../../../hooks/useUsdTokenSync";
import { ceilUsd, floorUsd } from "../../../components/utils/formatUsdAmount";
import { ceilToDecimals, roundToDecimals, truncateToDecimals, isScientific } from "../../../components/utils/RoundDecimals";
const MinMax = (props) => {
    const { setFieldValue, values } = useFormikContext();
    const { fromCurrency, from, limitsMinAmount, limitsMaxAmount, limitsMinAmountInUsd, limitsMaxAmountInUsd, onActionHover, depositMethod } = props;
    const isUsdMode = useUsdModeStore(s => s.isUsdMode);
    const setUsdAmount = useUsdModeStore(s => s.setUsdAmount);
    const selectedSourceAccount = useSelectedAccount("from", from?.name);
    const { wallets } = useWallet(from, 'withdrawal');
    const wallet = wallets.find(w => w.id === selectedSourceAccount?.id);
    const { gasData } = useSWRGas(selectedSourceAccount?.address, from, fromCurrency, values.amount, wallet);
    const { balances, mutate: mutateBalances } = useBalance(selectedSourceAccount?.address, from);
    const walletBalance = useMemo(() => {
        return selectedSourceAccount?.address ? balances?.find(b => b?.network === from?.name && b?.token === fromCurrency?.symbol) : undefined;
    }, [selectedSourceAccount?.address, balances, from?.name, fromCurrency?.symbol]);
    const gasAmount = gasData?.gas || 0;
    const native_currency = gasData?.token || from?.token;
    const shouldPayGasWithTheToken = (native_currency?.symbol === fromCurrency?.symbol) || !native_currency;
    const fallbackAmount = useMemo(() => {
        const raw = fromCurrency.price_in_usd > 0 ? 0.01 / fromCurrency.price_in_usd : 0.01;
        return roundToDecimals(raw, fromCurrency.decimals);
    }, [fromCurrency.price_in_usd, fromCurrency.decimals]);
    let maxAllowedAmount = useMemo(() => {
        return resolveMaxAllowedAmount({ fromCurrency, limitsMaxAmount, walletBalance, gasAmount, native_currency, depositMethod, fallbackAmount }) || 0;
    }, [fromCurrency, limitsMaxAmount, walletBalance, gasAmount, native_currency, depositMethod, fallbackAmount]);
    const minAmount = useMemo(() => {
        if (walletBalance && walletBalance.amount !== undefined && limitsMinAmount !== undefined && depositMethod === 'wallet') {
            return Number(walletBalance.amount) < limitsMinAmount ? Number(walletBalance.amount) : limitsMinAmount;
        }
        return limitsMinAmount || fallbackAmount;
    }, [walletBalance, limitsMinAmount, fallbackAmount, depositMethod]);
    const halfOfBalance = useMemo(() => {
        const base = walletBalance?.amount || maxAllowedAmount;
        // Truncate, not nearest-round: half of the balance must never exceed it.
        return base ? Number(truncateToDecimals(String(base / 2), fromCurrency.decimals)) : 0;
    }, [walletBalance?.amount, maxAllowedAmount, fromCurrency.decimals]);
    const handleSetValue = (value, usdValue) => {
        mutateBalances();
        const rounded = roundToDecimals(Number(value), fromCurrency?.decimals);
        const sanitizedValue = value !== '' && !isNaN(Number(value))
            ? (isScientific(rounded)
                ? rounded.toFixed(fromCurrency?.decimals ?? 0).replace(/\.?0+$/, '')
                : rounded.toString())
            : value;
        if (isUsdMode && usdValue) {
            // Only skip sync if the amount will actually change,
            // otherwise the sync effect won't fire to clear the flag.
            if (values.amount !== sanitizedValue) {
                skipNextUsdSync();
            }
            setUsdAmount(usdValue);
        }
        setFieldValue('amount', sanitizedValue, true);
        onActionHover(undefined);
    };
    const minIsFromLimits = limitsMinAmount !== undefined && Math.abs(minAmount - limitsMinAmount) < 1e-10;
    const maxIsFromLimits = limitsMaxAmount !== undefined && Math.abs(maxAllowedAmount - limitsMaxAmount) < 1e-10;
    // Quantize toward the safe side of each validation boundary: a backend
    // minimum rounds up (nearest could dip below it and fail immediately),
    // while a min derived from the wallet balance — like every max — is an
    // upper bound and truncates so the set value never exceeds it.
    const minValue = minIsFromLimits
        ? ceilToDecimals(minAmount, fromCurrency.decimals)
        : Number(truncateToDecimals(String(minAmount), fromCurrency.decimals));
    const maxValue = Number(truncateToDecimals(String(maxAllowedAmount), fromCurrency.decimals));
    const minUsdFormatted = minIsFromLimits && limitsMinAmountInUsd != undefined ? ceilUsd(limitsMinAmountInUsd) : undefined;
    const maxUsdFormatted = maxIsFromLimits && limitsMaxAmountInUsd != undefined ? floorUsd(limitsMaxAmountInUsd) : undefined;
    const handleSetMinAmount = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSetValue(minValue.toString(), minUsdFormatted);
    };
    const handleSetHalfAmount = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSetValue(halfOfBalance.toString());
    };
    const handleSetMaxAmount = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSetValue(maxValue.toString(), maxUsdFormatted);
    };
    const showMaxTooltip = !!(depositMethod === 'wallet' && walletBalance?.amount && shouldPayGasWithTheToken && (!limitsMaxAmount || walletBalance.amount < limitsMaxAmount));
    if (!from || !fromCurrency)
        return null;
    return (_jsxs("div", { className: "flex gap-1.5 group text-xs leading-4", onMouseLeave: () => onActionHover(undefined), children: [_jsx(ActionButton, { "data-attr": "min-amount", label: "Min", onMouseEnter: () => onActionHover(minValue, minUsdFormatted), onClick: handleSetMinAmount }), _jsx(ActionButton, { "data-attr": "half-amount", label: "50%", onMouseEnter: () => onActionHover(halfOfBalance), onClick: handleSetHalfAmount }), _jsxs(Tooltip, { disableHoverableContent: true, children: [_jsx(TooltipTrigger, { asChild: true, children: _jsx(ActionButton, { "data-attr": "max-amount", label: "Max", onMouseEnter: () => onActionHover(maxValue, maxUsdFormatted), onClick: handleSetMaxAmount }) }), showMaxTooltip ? _jsx(TooltipContent, { className: "pointer-events-none w-80 grow p-2 border-none! bg-secondary-300! text-xs rounded-xl!", side: "top", align: "start", alignOffset: -10, children: _jsx("p", { children: "Max is calculated based on your balance minus gas fee for the transaction" }) }) : null] })] }));
};
export default MinMax;
const ActionButton = React.forwardRef(({ label, onClick, onMouseEnter, disabled, ...rest }, ref) => {
    return (_jsx("button", { ...rest, ref: ref, onMouseEnter: onMouseEnter, onClick: onClick, type: "button", disabled: disabled, className: "px-1.5 py-0.5 rounded-md duration-200 break-keep transition bg-secondary-300 hover:bg-secondary-200 text-secondary-text hover:text-primary-buttonTextColor cursor-pointer enabled:active:animate-press-down", children: label }));
});
